import { useEffect, useMemo, useState } from "react";
import { QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { FilterItem } from "@/modules/common/filters";

const useOptionsQuery = <TPage>(
  queryKey: QueryKey,
  queryFn: (pageParam: number, search: string | undefined) => Promise<TPage>,
  mapToOptions: (page: TPage) => FilterItem[],
  getNextPageParam: (lastPage: TPage) => number | undefined,
  errorMessage: string,
  currentValue?: FilterItem,
  enabled?: boolean,
) => {
  const [search, setSearch] = useState<string>();

  const query = useInfiniteQuery({
    queryKey: [...queryKey, search],
    queryFn: ({ pageParam }) => queryFn(pageParam, search),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => getNextPageParam(lastPage),
    retry: false,
    staleTime: 5 * 60_000,
    enabled,
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(query.error?.message || errorMessage);
    }
  }, [query.isError, query.error]);

  const currentValueKey = currentValue ? `${currentValue.value}|${currentValue.label}` : undefined;

  const options = useMemo(() => {
    const loaded = query.data?.pages.flatMap(mapToOptions) ?? [];

    if (currentValue && !loaded.some((option) => option.value === currentValue.value)) {
      return [currentValue, ...loaded];
    }

    return loaded;
  }, [query.data, currentValueKey]);

  return {
    options,
    isLoading: query.isFetching,
    hasMore: query.hasNextPage,
    onLoadMore: () => query.fetchNextPage(),
    onSearch: (value: string) => setSearch(value || undefined),
    pages: query.data?.pages ?? ([] as TPage[]),
  };
};

export default useOptionsQuery;
